import React from 'react';
import { Card } from '../../components/ui';
import { FileText, Eye, EyeOff } from 'lucide-react';
import type { ResumeText } from '../../types';

interface ResumeTextPreviewProps {
  text: ResumeText | null;
  isLoading?: boolean;
  maxLines?: number;
  className?: string;
}

export function ResumeTextPreview({ 
  text, 
  isLoading = false, 
  maxLines = 15,
  className = "" 
}: ResumeTextPreviewProps) {
  const [isExpanded, setIsExpanded] = React.useState(false);

  if (isLoading) {
    return (
      <Card className={className}>
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-muted-foreground" />
            <h3 className="font-semibold text-foreground">Text Preview</h3>
          </div>
          <div className="space-y-2">
            {/* Loading skeleton */}
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                className="h-4 bg-muted rounded animate-pulse"
                style={{ width: `${Math.random() * 40 + 60}%` }}
              />
            ))}
          </div>
        </div>
      </Card>
    );
  }

  if (!text) {
    return (
      <Card className={className}>
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-muted-foreground" />
            <h3 className="font-semibold text-foreground">Text Preview</h3>
          </div>
          <div className="text-center py-8">
            <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
            <p className="text-muted-foreground">No text available yet</p>
            <p className="text-sm text-muted-foreground">
              Text will appear here once parsing completes
            </p>
          </div>
        </div>
      </Card>
    );
  }

  // Split content into lines and limit display
  const lines = text.content.split('\n');
  const displayLines = isExpanded ? lines : lines.slice(0, maxLines);
  const hasMoreLines = lines.length > maxLines;

  return (
    <Card className={className}>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            <h3 className="font-semibold text-foreground">Text Preview</h3>
          </div>
          <div className="text-xs text-muted-foreground">
            {text.word_count.toLocaleString()} words
          </div>
        </div>

        {/* Content */}
        <div className="relative">
          <div className="bg-muted/30 rounded-lg p-4 font-mono text-sm leading-relaxed max-h-96 overflow-y-auto">
            {displayLines.map((line, index) => (
              <div key={index} className="text-foreground">
                {line.trim() || '\u00A0'} {/* Non-breaking space for empty lines */}
              </div>
            ))}
            
            {/* Gradient fade for collapsed state */}
            {!isExpanded && hasMoreLines && (
              <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-muted/30 to-transparent pointer-events-none" />
            )}
          </div>

          {/* Expand/Collapse Button */}
          {hasMoreLines && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="mt-2 flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors"
            >
              {isExpanded ? (
                <>
                  <EyeOff className="h-4 w-4" />
                  Show Less
                </>
              ) : (
                <>
                  <Eye className="h-4 w-4" />
                  Show More ({lines.length - maxLines} more lines)
                </>
              )}
            </button>
          )}
        </div>

        {/* Footer Info */}
        <div className="flex justify-between items-center text-xs text-muted-foreground">
          <span>
            Last modified: {new Date(text.last_modified).toLocaleString()}
          </span>
          <span>
            Version {text.version}
          </span>
        </div>
      </div>
    </Card>
  );
}